import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import {
  trans,
  FORM_FULL_NAME_REQUIRED,
  FORM_SOURCE_REQUIRED,
  FORM_SAVE_ERROR,
  FORM_DUPLICATE_BLOCKED,
  DOB_INVALID_DATE_ERROR
} from '../../translator'

const DOB_FIELD_NAMES = ['dob_year_from', 'dob_year_to', 'dob_month', 'dob_day']

const CREATE_URL = '/internal/api/associated-person'
const UPDATE_URL = '/internal/api/associated-person'
const REDIRECT_BASE = '/associated-persons'
const ISSUE_REDIRECT_BASE = '/issue/show'

const MULTI_VALUE_FIELDS = ['aliases', 'phone_numbers', 'emails', 'addresses', 'social_media_handles']

interface DateOfBirthParts {
  year_from: number | null
  year_to: number | null
  month: number | null
  day: number | null
}

interface SavePayload {
  full_name: string
  date_of_birth_parts: DateOfBirthParts
  status: string
  source: string | null
  source_other: string | null
  criminal_history_flags: string[]
  criminal_history_narrative: string | null
  threat_assessment: string | null
  actions_taken: string | null
  person_notes: string | null
  is_banned: boolean
  is_forcing_duplicate: boolean
  [key: string]: unknown
}

/**
 * Drives the Associated Person create/edit form. Collects every field (including
 * the repeatable multi-value groups, the flag checkbox sets, and the date-of-birth
 * components read from the dob-entry controller's own fields), enforces the single
 * mandatory field (full name) client-side, and submits to the session-authenticated
 * create (POST) or update (PATCH) endpoint depending on whether a record id is
 * present. Also handles the Source "Other" free-text reveal.
 */
export default class AssociatedPersonFormController extends Controller {
  static targets = ['fullName', 'status', 'source', 'sourceOther', 'sourceOtherWrapper', 'alert', 'isBanned']
  static values = { recordId: String }

  declare readonly fullNameTarget: HTMLInputElement
  declare readonly statusTarget: HTMLSelectElement
  declare readonly sourceTarget: HTMLSelectElement
  declare readonly hasSourceTarget: boolean
  declare readonly sourceOtherTarget: HTMLInputElement
  declare readonly hasSourceOtherTarget: boolean
  declare readonly sourceOtherWrapperTarget: HTMLElement
  declare readonly hasSourceOtherWrapperTarget: boolean
  declare readonly alertTarget: HTMLElement
  declare readonly hasAlertTarget: boolean
  declare readonly isBannedTarget: HTMLInputElement
  declare readonly hasIsBannedTarget: boolean
  declare readonly recordIdValue: string

  private isForcingDuplicate = false

  /** Records the user's choice to proceed past the duplicate warning so the next save bypasses screening. */
  forceDuplicate (): void {
    this.isForcingDuplicate = true
  }

  /** A changed name or date-of-birth component is a different subject to screen, so a Proceed given for a previous one must not carry over. */
  resetForcedDuplicate (): void {
    this.isForcingDuplicate = false
  }

  connect (): void {
    this.toggleSourceOther()
  }

  /** Shows the free-text source input only when "Other" is selected. */
  toggleSourceOther (): void {
    if (!this.hasSourceTarget || !this.hasSourceOtherWrapperTarget) {
      return
    }

    const isOther = this.sourceTarget.value === 'other'
    this.sourceOtherWrapperTarget.classList.toggle('d-none', !isOther)

    if (!isOther && this.hasSourceOtherTarget) {
      this.sourceOtherTarget.value = ''
    }
  }

  save (): void {
    this.submit().catch(() => this.showAlert(trans(FORM_SAVE_ERROR, {}, 'associated-persons')))
  }

  private async submit (): Promise<void> {
    this.hideAlert()

    if (this.value(this.fullNameTarget) === '') {
      this.showAlert(trans(FORM_FULL_NAME_REQUIRED, {}, 'associated-persons'))

      return
    }

    if (this.isSourceRequired() && (!this.hasSourceTarget || this.sourceTarget.value === '')) {
      this.showAlert(trans(FORM_SOURCE_REQUIRED, {}, 'associated-persons'))

      return
    }

    if (this.hasInvalidDateOfBirthComponent()) {
      this.showAlert(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return
    }

    const isEdit = this.recordIdValue !== ''
    const url = isEdit ? `${UPDATE_URL}/${this.recordIdValue}` : CREATE_URL
    const method = isEdit ? 'PATCH' : 'POST'

    const payload = this.buildPayload()

    try {
      const response = await fetchWithHandlingLoginRedirect(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const data = await response.json().catch(() => ({})) as { result?: { id?: string }, status?: string }

      if (!response.ok) {
        this.showSaveFailure(response.status, payload.source)

        return
      }

      if (data.status === 'duplicate_match') {
        this.showAlert(trans(FORM_DUPLICATE_BLOCKED, {}, 'associated-persons'))

        return
      }

      const id = isEdit ? this.recordIdValue : data.result?.id
      const issueIdInput = this.element.querySelector<HTMLInputElement>('[name="issue_id"]')
      const issueId = issueIdInput !== null && issueIdInput.value !== '' ? issueIdInput.value : null

      if (!isEdit && issueId) {
        this.redirect(`${ISSUE_REDIRECT_BASE}/${issueId}`)
      } else if (typeof id === 'string' && id !== '') {
        this.redirect(`${REDIRECT_BASE}/${id}`)
      }
    } catch (error) {
      // fetchWithHandlingLoginRedirect rejects with the Response on a non-2xx status, so a 400
      // lands here rather than in the !response.ok branch above.
      this.showSaveFailure((error as { status?: number })?.status, payload.source)
    }
  }

  /**
   * Source is mandatory only when creating a standalone record — i.e. a new person
   * (no record id) that isn't being created from an Issue. Editing never requires a
   * source, and creating from an Issue leaves it optional, mirroring the backend rule.
   */
  private isSourceRequired (): boolean {
    if (this.recordIdValue !== '') {
      return false
    }

    const issueIdInput = this.element.querySelector<HTMLInputElement>('[name="issue_id"]')

    return issueIdInput === null || issueIdInput.value === ''
  }

  /**
   * A standalone record (no linked Issue) is rejected with a 400 when no source is given,
   * so explain that specifically rather than showing the generic error. When an Issue is
   * linked the source is optional, so a 400 is something else and gets the generic message.
   */
  private showSaveFailure (status: number | undefined, source: string | null): void {
    const requiresSource = status === 400 && source === null && this.isSourceRequired()
    this.showAlert(trans(requiresSource ? FORM_SOURCE_REQUIRED : FORM_SAVE_ERROR, {}, 'associated-persons'))
  }

  private buildPayload (): SavePayload {
    const payload: SavePayload = {
      full_name: this.value(this.fullNameTarget),
      date_of_birth_parts: this.buildDateOfBirthParts(),
      status: this.statusTarget.value,
      source: this.hasSourceTarget && this.sourceTarget.value !== '' ? this.sourceTarget.value : null,
      source_other: this.hasSourceOtherTarget && this.sourceOtherTarget.value.trim() !== '' ? this.sourceOtherTarget.value.trim() : null,
      criminal_history_flags: this.collectChecked('criminal_history_flags'),
      criminal_history_narrative: this.collectText('criminal_history_narrative'),
      threat_assessment: this.collectText('threat_assessment'),
      actions_taken: this.collectText('actions_taken'),
      person_notes: this.collectText('person_notes'),
      is_banned: this.hasIsBannedTarget && this.isBannedTarget.checked,
      is_forcing_duplicate: this.isForcingDuplicate
    }

    MULTI_VALUE_FIELDS.forEach(field => {
      payload[field] = this.collectValues(field)
    })

    // Present only when reached from an Issue's "Create new record"; links the new person to that Issue.
    const issueIdInput = this.element.querySelector<HTMLInputElement>('[name="issue_id"]')
    if (issueIdInput !== null) {
      payload.issue_id = issueIdInput.value
    }

    // Only Tier-3 forms render the photos UI; omit the photo fields otherwise so a
    // non-Tier-3 save never reconciles (and thereby deletes) the person's photos.
    const primaryInput = this.element.querySelector<HTMLInputElement>('[name="primary_media_id"]')
    if (primaryInput !== null) {
      payload.photo_media_ids = this.collectValues('photo_media_ids')
      payload.primary_media_id = primaryInput.value !== '' ? primaryInput.value : null
    }

    return payload
  }

  /**
   * Reads the four date-of-birth components straight off the dob-entry controller's
   * own fields by name, the same way every other field here is read, rather than
   * calling into that controller directly. Called only once hasInvalidDateOfBirthComponent
   * has confirmed every held value is empty or a whole number, so a bad value cannot
   * reach here and be silently sent as null.
   */
  private buildDateOfBirthParts (): DateOfBirthParts {
    const numberOrNull = (name: string): number | null => {
      const value = this.rawDobValue(name)

      return value === '' ? null : Number(value)
    }

    return {
      year_from: numberOrNull('dob_year_from'),
      year_to: numberOrNull('dob_year_to'),
      month: numberOrNull('dob_month'),
      day: numberOrNull('dob_day')
    }
  }

  /**
   * Guards buildDateOfBirthParts against a stray non-numeric value in one of the four dob
   * fields (day and month are free-text boxes read directly by name, independent of the
   * dob-entry controller's own on-blur validation) — without this, Number() on something
   * like "19x8" is NaN, which JSON.stringify emits as null, silently downgrading the saved
   * precision instead of refusing the save.
   */
  private hasInvalidDateOfBirthComponent (): boolean {
    return DOB_FIELD_NAMES.some(name => {
      const value = this.rawDobValue(name)

      return value !== '' && !Number.isInteger(Number(value))
    })
  }

  private rawDobValue (name: string): string {
    return this.element.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value.trim() ?? ''
  }

  private collectValues (field: string): string[] {
    return Array.from(this.element.querySelectorAll<HTMLInputElement>(`[name="${field}[]"]`))
      .map(input => input.value.trim())
      .filter(value => value !== '')
  }

  private collectChecked (field: string): string[] {
    return Array.from(this.element.querySelectorAll<HTMLInputElement>(`[name="${field}[]"]:checked`))
      .map(input => input.value)
  }

  private collectText (field: string): string | null {
    const el = this.element.querySelector<HTMLTextAreaElement>(`[name="${field}"]`)
    const value = el?.value.trim() ?? ''

    return value === '' ? null : value
  }

  private value (input: HTMLInputElement): string {
    return input.value.trim()
  }

  redirect (url: string): void {
    window.location.href = url
  }

  private showAlert (message: string): void {
    if (!this.hasAlertTarget) {
      return
    }

    this.alertTarget.textContent = message
    this.alertTarget.classList.remove('d-none')
  }

  private hideAlert (): void {
    if (!this.hasAlertTarget) {
      return
    }

    this.alertTarget.textContent = ''
    this.alertTarget.classList.add('d-none')
  }
}
